'use client';

import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useTranslations } from 'use-intl';
import { classnames } from '@/utils/classnames/classnames';

export type ListSummary = { id: string; name: string; itemCount: number; isDefault?: boolean };

interface ListSwitcherProps {
  lists: ListSummary[];
  activeId: string;
  onSelect: (id: string) => void;
}

const ListSwitcher = ({ lists, activeId, onSelect }: ListSwitcherProps) => {
  const translate = useTranslations('dashboard');

  return (
    <Menu as="div" className="relative">
      <MenuButton className="text-14 text-primary inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 font-semibold transition-colors hover:bg-gray-50">
        <span>{translate('purchase-list-change-list')}</span>
        <ChevronDownIcon className="h-4 w-4 transition-transform data-[open]:rotate-180" aria-hidden />
      </MenuButton>

      <MenuItems
        // EWM-771 — non-modal dropdown; skip headlessui's default body-scroll lock so
        // opening the menu doesn't hide the page scrollbar and shift content sideways.
        modal={false}
        // EWM-778 — anchor via Floating UI so the panel always shifts to stay within the
        // viewport regardless of the trigger's position (it's the leftmost item in a
        // right-aligned cluster that wraps differently across mobile widths, so a static
        // CSS anchor can't stay in-bounds everywhere). `padding` keeps an 8px viewport
        // gutter; max-w guards ultra-narrow screens.
        anchor={{ to: 'bottom end', gap: '0.25rem', padding: '0.5rem' }}
        // headlessui's anchor portals the panel to <body>, OUTSIDE the app's
        // [data-theme='default'] wrapper (app/[locale]/layout.tsx) where the theme CSS
        // vars live. Re-declare the theme on the panel so tokens (text-primary,
        // bg-primary/5, border-gray-200, the green badge) resolve instead of falling back.
        data-theme="default"
        transition
        className="z-20 w-72 max-w-[calc(100vw-1rem)] origin-top-right overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg transition duration-100 ease-out focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0"
      >
        <p className="text-12 border-b border-gray-100 bg-gray-50 px-4 py-2 font-semibold tracking-wide text-gray-500 uppercase">
          {translate('purchase-list-switch-list')}
        </p>
        {lists.map((list) => (
          <MenuItem key={list.id}>
            {({ focus }) => (
              <button
                type="button"
                onClick={() => onSelect(list.id)}
                className={classnames('flex w-full flex-col items-start px-4 py-3 text-left transition-colors', {
                  'bg-gray-50': focus && list.id !== activeId,
                  'bg-primary/5': list.id === activeId,
                })}
              >
                <span className="flex items-center gap-2">
                  <span
                    className={classnames('text-14 font-semibold', {
                      'text-primary': list.id === activeId,
                      'text-gray-800': list.id !== activeId,
                    })}
                  >
                    {list.name}
                  </span>
                  {list.isDefault && (
                    <span className="text-11 inline-block rounded-full bg-green-100 px-2 py-0.5 font-semibold text-green-700">
                      {translate('purchase-list-default-badge')}
                    </span>
                  )}
                </span>
                <span className="text-12 text-gray-500">
                  {translate('purchase-list-item-count', { count: list.itemCount })}
                </span>
              </button>
            )}
          </MenuItem>
        ))}
      </MenuItems>
    </Menu>
  );
};

export default ListSwitcher;
